/**
 * Portal ICAVI - Guarda de conectividade (circuit breaker leve)
 *
 * Centraliza o estado transitório de comunicação com o backend para evitar que
 * múltiplos pollers (notificações, avisos, push, calendário) inundem o console
 * com erros repetidos durante:
 *   - indisponibilidade de rede/proxy ("Network Error" sem resposta HTTP);
 *   - sessão já invalidada (cascata de 401 antes do logout/redirect).
 *
 * Não altera o fluxo das requisições: apenas registra estado e decide se um
 * erro transitório esperado deve ser logado por chamada ou apenas uma vez,
 * de forma centralizada e com throttle.
 *
 * O estado é reposto automaticamente a cada resposta bem-sucedida, de modo que
 * após um novo login o logging normal volta a funcionar sem estado preso.
 */
import axios from 'axios';
/** Janela em que o backend é considerado indisponível após um erro de rede. */
const OUTAGE_BACKOFF_MS = 30 * 1000;
/** Intervalo mínimo entre logs centrais de indisponibilidade. */
const OUTAGE_LOG_THROTTLE_MS = 30 * 1000;
let outageUntil = 0;
let outageLastLogAt = 0;
let sessionInvalidated = false;
/**
 * Erro "sem resposta": conectividade/proxy/timeout. Cancelamentos não contam.
 */
export const isNetworkError = (error) => {
    if (axios.isCancel(error)) {
        return false;
    }
    if (!axios.isAxiosError(error)) {
        return false;
    }
    if (error.response) {
        return false;
    }
    return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.code;
};
const is401 = (error) => axios.isAxiosError(error) && error.response?.status === 401;
export const connectivityGuard = {
    /** Registra sucesso de comunicação: encerra outage e revalida a sessão. */
    recordSuccess() {
        outageUntil = 0;
        sessionInvalidated = false;
    },
    /** Registra erro de rede: abre a janela de indisponibilidade. */
    recordNetworkError() {
        outageUntil = Date.now() + OUTAGE_BACKOFF_MS;
    },
    /** Marca a sessão como invalidada (após force-logout / refresh rejeitado). */
    markSessionInvalidated() {
        sessionInvalidated = true;
    },
    /** Limpa o estado de sessão invalidada (novo login/token válido). */
    clearSessionInvalidated() {
        sessionInvalidated = false;
    },
    isBackendUnavailable() {
        return Date.now() < outageUntil;
    },
    isSessionInvalidated() {
        return sessionInvalidated;
    },
    /**
     * Indica se o erro é uma condição transitória esperada — indisponibilidade de
     * backend ou 401 de sessão já invalidada — que é tratada centralmente e NÃO
     * deve ser logada repetidamente por cada chamador.
     */
    isExpectedTransient(error) {
        if (isNetworkError(error)) {
            return true;
        }
        if (is401(error) && sessionInvalidated) {
            return true;
        }
        return false;
    },
    /**
     * Decide se o log central de indisponibilidade deve ser emitido agora
     * (no máximo uma vez por janela de throttle).
     */
    shouldLogOutageOnce() {
        const agora = Date.now();
        if (agora - outageLastLogAt < OUTAGE_LOG_THROTTLE_MS) {
            return false;
        }
        outageLastLogAt = agora;
        return true;
    },
};
export default connectivityGuard;
